import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Play, Radio } from "lucide-react";
import { BigHitsGrid, BigHitModal } from "@/pages/BigHits";

export default function LiveStreams() {
  const [schedule, setSchedule] = useState([]);
  const [settings, setSettings] = useState({});
  const [hits, setHits] = useState([]);
  const [activeHit, setActiveHit] = useState(null);

  useEffect(() => {
    api.get("/schedule")
      .then((r) => setSchedule(r.data))
      .catch((err) => console.warn("Failed to load schedule:", err?.message));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/big-hits?limit=8").then((r) => setHits(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/5">
        <img src="https://images.pexels.com/photos/30053813/pexels-photo-30053813.jpeg"
             className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.5), rgba(10,10,10,0.95))" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="flex items-center gap-3">
            <span className="pulse-dot" />
            <span className="text-label text-[var(--cl-primary)]">{settings.live_kicker || "Live Auctions · Almost Nightly on Whatnot"}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl mt-3">
            {settings.live_headline_1 || "Auctions."}<br/>{settings.live_headline_2 || "Live. Nightly."}
          </h1>
          <p className="text-zinc-300 mt-4 max-w-xl">
            {settings.live_subtitle || "Mystery pack auctions, singles auctions, and the occasional case break — hosted live on Whatnot. Follow along on TikTok for highlights and browse the eBay store between shows."}
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <a href={settings.social_whatnot_url || "https://whatnot.com"} target="_blank" rel="noreferrer" data-testid="live-join-whatnot" className="btn-primary">
              <Play size={16} className="inline mr-2 -mt-1" /> {settings.live_cta_whatnot || "Watch Live on Whatnot"}
            </a>
            <a href={settings.social_tiktok_url || "https://tiktok.com"} target="_blank" rel="noreferrer" data-testid="live-tiktok" className="btn-outline">
              {settings.live_cta_tiktok || "Follow on TikTok"}
            </a>
            <a href={settings.social_ebay_url || "https://ebay.com"} target="_blank" rel="noreferrer" data-testid="live-ebay" className="btn-outline">
              {settings.live_cta_ebay || "Shop on eBay"}
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-label text-zinc-500">Weekly Auction Schedule</div>
            <h2 className="font-display text-4xl mt-2">Where to Bid</h2>
            <p className="text-zinc-400 text-sm mt-3">Times vary occasionally. Follow us on Whatnot to get show-start alerts on your phone.</p>
            <ul className="mt-6 space-y-3">
              {schedule.length === 0 && <li className="text-zinc-500 text-sm">Schedule coming soon.</li>}
              {schedule.map((s) => <ScheduleRow key={s.id || `${s.day}-${s.title}`} {...s} />)}
            </ul>
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-label text-zinc-500">Recent Wins</div>
                <h2 className="font-display text-4xl mt-2">Big Hits Gallery</h2>
              </div>
              <Link to="/big-hits" className="text-label text-[var(--cl-orange)] hover:underline" data-testid="live-view-all-hits">See All →</Link>
            </div>
            <p className="text-zinc-400 text-sm mt-3">Auction wins & mystery pack hits from the community. Add clips from <span className="text-white">Admin → Big Hits</span>.</p>
            <div className="mt-6" data-testid="live-big-hits">
              {hits.length === 0
                ? <div className="border border-dashed border-white/10 rounded p-8 text-center text-zinc-500 text-sm">
                    No hits yet — add clips from the admin dashboard.
                  </div>
                : <BigHitsGrid hits={hits} onOpen={setActiveHit} />}
            </div>
          </div>
        </div>
      </section>
      <BigHitModal hit={activeHit} onClose={() => setActiveHit(null)} />
    </div>
  );
}

function ScheduleRow({ day, time, title, platform }) {
  return (
    <li className="flex items-center gap-4 border-l-2 border-[var(--cl-primary)] pl-4 py-1">
      <div className="text-center min-w-[52px]">
        <div className="font-display text-2xl leading-none">{day}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{time}</div>
      </div>
      <div className="flex-1">
        <div className="text-white">{title}</div>
        <div className="text-label text-[var(--cl-orange)] mt-1 flex items-center gap-1"><Radio size={10}/> {platform}</div>
      </div>
    </li>
  );
}
